import {
  InformalFeedbackRequest,
  InformalFeedbackRequestState,
  InformalFeedbackReviewState,
} from 'api/clients/InformalFeedbackRequestsClient'
import { useAuth } from 'hooks/UseAuth'
import { RequesterCard } from './RequesterCard'
import { ReviewerSubmittedCard } from './ReviewerSubmittedCard'
import { ReviewerPendingCard } from './ReviewerPendingCard'
import { last } from 'lodash'
import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { useSearchParams } from 'hooks/useSearchParams'
import { useModals } from 'almanac-ui/Modal'
import { EndReviewModal } from 'components/ReviewSummaryModal/EndReviewModal/EndReviewModal'
import { useHistory } from 'react-router-dom'
import { RequestAnotherReviewModal } from 'components/ReviewSummaryModal/RequestAnotherReviewModal/RequestAnotherReviewModal'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { DesignChannel } from 'components/NewFileModal/Types'
import { RequestEndedCard } from './RequestEndedCard'
import { useIsChannelPostable } from 'components/PostStatusDropdown/utils'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'

interface Props {
  request: InformalFeedbackRequest
  channel?: ChannelType | DesignChannel
  assetType: 'article' | 'design'
  onDismiss: (value: boolean) => void
}

export const ReviewRequestCard = observer(({ request, channel, assetType, onDismiss }: Props) => {
  const { designStore } = useRootStore()
  const auth = useAuth()
  const { openModal } = useModals()
  const searchParams = useSearchParams()
  const history = useHistory()
  const { isChannelSocialPostable } = useIsChannelPostable()

  const isPostableOrDesign = isChannelSocialPostable(channel) || !!request.design

  const submitReview = searchParams.get('submitReview')
  const endRequest = searchParams.get('endRequest')
  const requestAnotherReview = searchParams.get('requestAnotherReview')

  useEffect(() => {
    if (!!submitReview) {
      searchParams.delete('submitReview')
    }

    if (!!endRequest && request) {
      openModal(EndReviewModal, { request })
      searchParams.delete('endRequest')
    }

    if (!!requestAnotherReview && request) {
      openModal(RequestAnotherReviewModal, { request })
      searchParams.delete('requestAnotherReview')
    }

    if (!!submitReview || !!endRequest || !!requestAnotherReview) {
      history.replace({ search: searchParams.toString() })
    }
  }, [submitReview, endRequest, openModal, request, searchParams, history, requestAnotherReview])

  if (!request || (assetType === 'design' && !designStore?.tab)) return null

  const isMyRequest = auth && request.requester.id === auth.id
  const requestIsClosed = request.state === InformalFeedbackRequestState.Closed
  const showRequestEndedCard = requestIsClosed && !request.cleanup_dismissed

  const reviews = request.reviews
  const reReviewRequestedToMe = reviews.find(
    (review) =>
      review.reviewer.id === auth?.id && review.state === InformalFeedbackReviewState.Pending && review.iteration > 0,
  )
  const reviewSubmittedOrDismissedByMe = last(
    reviews.filter((review) => review.reviewer.id === auth?.id && review.state !== InformalFeedbackReviewState.Pending),
  )
  if (showRequestEndedCard) {
    return (
      <>
        <ResponsiveDesign.Desktop>
          <RequestEndedCard request={request} onDismiss={onDismiss} />
        </ResponsiveDesign.Desktop>
        <ResponsiveDesign.Mobile>
          {!isPostableOrDesign && <RequestEndedCard request={request} onDismiss={onDismiss} />}
        </ResponsiveDesign.Mobile>
      </>
    )
  }

  if (requestIsClosed) return null

  if (isMyRequest) return <RequesterCard request={request} isPostableOrDesign={isPostableOrDesign} />

  // authed user is a reviewer, they have sent the review back for changes and got requested another review
  if (!!reReviewRequestedToMe) {
    return (
      <ReviewerPendingCard
        request={request}
        isPostableOrDesign={isPostableOrDesign}
        reRequestMessage={reReviewRequestedToMe.informal_feedback_request_message}
      />
    )
  }

  // authed user is a reviewer and they submitted the review
  if (!!reviewSubmittedOrDismissedByMe) {
    return (
      <>
        <ResponsiveDesign.Desktop>
          <ReviewerSubmittedCard request={request} review={reviewSubmittedOrDismissedByMe} />
        </ResponsiveDesign.Desktop>
        <ResponsiveDesign.Mobile>
          {!isPostableOrDesign && <ReviewerSubmittedCard request={request} review={reviewSubmittedOrDismissedByMe} />}
        </ResponsiveDesign.Mobile>
      </>
    )
  }

  // authed user is a reviewer and they have not submitted or dismissed the review yet
  if (!reviewSubmittedOrDismissedByMe)
    return <ReviewerPendingCard request={request} isPostableOrDesign={isPostableOrDesign} />

  return null
})
